import { Observable, Subscription } from 'rxjs';
import type { FamilyMemberEditFailure } from '../../services/family-member-edit.service';
import type { FamilyMemberErrors } from '../../components/notification-modal/utils/notification-family-sale.util';
import type {
  FamilyFormOption,
  FamilyFormPrice,
  FamilyMemberFormModel,
} from '../types/family-member-form.types';
import type { FamilyPlanMemberView } from '../types/family-plan-view.types';
import {
  editChangeOf,
  editDiscountOptions,
  editModelOf,
  editPackageOptions,
  editPrice,
  type FamilyMemberEditChange,
  hasEditChange,
  hasEditErrors,
  otherPlates,
  validateFamilyMemberEdit,
} from './family-member-edit.util';

/** The save this controller makes; implemented by FamilyMemberEditService. */
export interface FamilyMemberEditActions {
  save(change: FamilyMemberEditChange): Observable<unknown>;
}

export interface FamilyMemberEditOptions {
  /** A save went through: reload the family. */
  onSaved?: () => void;
  /** Part of a save went through and the rest failed: reload the family, keep the form. */
  onPartial?: () => void;
}

/**
 * State behind the Family Plan tab's inline Edit Details form: which member is open, its values and
 * errors, and the save. One member is edited at a time. The template renders it and calls back.
 */
export class FamilyMemberEditController {
  /** The member being edited, or null. */
  member: FamilyPlanMemberView | null = null;
  contactLocked = false;
  model: FamilyMemberFormModel | null = null;
  errors: FamilyMemberErrors = {};
  /** Package, discount and price are shown, not edited: they are billing (see CWS-1579 notes). */
  packages: FamilyFormOption[] = [];
  discounts: FamilyFormOption<number>[] = [];
  price: FamilyFormPrice | null = null;
  busy = false;
  /** A refusal from the server, as text. */
  error = '';

  private others: string[] = [];
  private request = Subscription.EMPTY;

  constructor(
    private readonly actions: FamilyMemberEditActions,
    private readonly options: FamilyMemberEditOptions = {}
  ) {}

  isOpen(memberId: string): boolean {
    return this.member?.member_id === memberId;
  }

  open(
    member: FamilyPlanMemberView,
    members: FamilyPlanMemberView[],
    contactLocked: boolean
  ): void {
    this.close();
    this.member = member;
    this.contactLocked = contactLocked;
    this.model = editModelOf(member);
    this.packages = editPackageOptions(member);
    this.discounts = editDiscountOptions(member);
    this.price = editPrice(member);
    this.others = otherPlates(members, member);
  }

  close(): void {
    this.request.unsubscribe();
    this.member = null;
    this.model = null;
    this.errors = {};
    this.error = '';
    this.busy = false;
  }

  save(): void {
    if (!this.member || !this.model || this.busy) {
      return;
    }
    this.errors = validateFamilyMemberEdit(this.model, this.others, this.contactLocked);
    if (hasEditErrors(this.errors)) {
      return;
    }
    const change = editChangeOf(this.member, this.model, this.contactLocked);
    if (!hasEditChange(change)) {
      this.close();
      return;
    }
    this.busy = true;
    this.error = '';
    this.request = this.actions.save(change).subscribe({
      next: () => {
        this.close();
        this.options.onSaved?.();
      },
      error: error => {
        this.busy = false;
        const partial = isFailure(error) && error.partial;
        this.error = partial
          ? `The plate was saved, but the name or phone was not. ${errorText(error)}`
          : errorText(error);
        if (partial) {
          this.options.onPartial?.();
        }
      },
    });
  }

  destroy(): void {
    this.request.unsubscribe();
  }
}

const isFailure = (error: unknown): error is FamilyMemberEditFailure =>
  typeof error === 'object' && error !== null && 'step' in error && 'cause' in error;

/**
 * The server's own words when it gave any. A 409 on the vehicle call is a plate another customer
 * holds; a 409 on the customer call is a phone that is taken, and its own message says so.
 */
function errorText(error: unknown): string {
  const failure = isFailure(error) ? error : null;
  const http = (failure?.cause ?? error) as { status?: number; error?: { message?: string } };
  if (failure?.step === 'vehicle' && http?.status === 409) {
    return 'This plate belongs to another customer.';
  }
  return http?.error?.message || 'The details could not be saved. Try again.';
}
